import { AuthorDTO } from '@/app/models/series.model';
import { Component, Input, OnInit, OnDestroy, ElementRef, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';

export type AuthorDisplayType = 'link' | 'inline';

@Component({
    selector: 'app-author-view',
    templateUrl: './author-view.component.html',
    styleUrls: ['./author-view.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AuthorViewComponent implements OnInit, OnDestroy {

  @Input() author!: AuthorDTO;
  @Input() display: AuthorDisplayType = 'link';

  @ViewChild('popoverRef', { static: false }) popoverRef!: ElementRef;

  // State management
  popover: boolean = false;
  pos: { x: number, y: number, width: number, height: number } = { x: 0, y: 0, width: 0, height: 0 };
  rightPlaced: boolean = false;
  private triggerEl: HTMLElement | null = null;
  private suppressCloseUntil = 0; // timestamp to ignore early close events

  private scrollListener?: () => void;
  private clickListener?: (event: MouseEvent) => void;
  private keyListener?: (event: KeyboardEvent) => void;

  constructor(
    private router: Router,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {

  }

  // Add getter to ensure we have valid author data
  get hasValidAuthor(): boolean {
    return !!(this.author && this.author.fullName);
  }

  ngOnInit(): void {    
    // Validate author data
    if (!this.author) {
      console.warn('AuthorViewComponent: No author data provided');
      return;
    }
    
    if (!this.author.fullName) {
      console.warn('AuthorViewComponent: Author missing fullName:', this.author);
    }
    
    this.setupScrollListener();
  }

  ngOnDestroy(): void {
    this.removeEventListeners();
  }

  // Setup scroll listener to close popover on scroll
  private setupScrollListener(): void {
    this.scrollListener = () => {
      if (!this.popover) return;
      // Ignore scroll events occurring immediately after open (layout shifts)
      if (Date.now() < this.suppressCloseUntil) return;
      this.closePopover();
    };
    document.addEventListener('scroll', this.scrollListener, false);
  }

  // Handle author click to show popover
  onAuthorClicked(event: MouseEvent): void {
    // Validate author data before proceeding
    if (!this.author) {
      console.error('No author data available');
      return;
    }

    event.preventDefault();
    event.stopPropagation();

  const element = event.currentTarget as HTMLElement;
  if (!element) {
      console.error('No current target element found');
      return;
    }

    const elementPos = element.getBoundingClientRect();

    // Calculate position and check if should be placed on right
    let posX = elementPos.x;
    let rightPlaced = false;

    if ((elementPos.x + 400) > window.innerWidth) {
      posX = elementPos.x - 400 + elementPos.width;
      if (posX < 15) {
        posX = 15;
      }
      rightPlaced = true;
    }

    this.pos = {
      x: posX,
      y: elementPos.y + elementPos.height + 5,
      width: elementPos.width,
      height: elementPos.height
    };
  this.rightPlaced = rightPlaced;
  this.popover = true;
  this.triggerEl = element;
  this.suppressCloseUntil = Date.now() + 250; // 250ms grace period

    this.cdr.detectChanges();

    // Add click listener to close popover when clicking outside
    setTimeout(() => {
      this.setupClickListener();
      this.setupKeyListener();
    }, 0);
  }

  private setupClickListener(): void {
    this.clickListener = (event: MouseEvent) => {
      if (!this.popover) return;
      // Ignore clicks during grace period
      if (Date.now() < this.suppressCloseUntil) return;
      const popEl = this.popoverRef?.nativeElement as HTMLElement | undefined;
      const target = event.target as Node;
      // If click is inside popover or on trigger element -> ignore
      if (popEl?.contains(target) || this.triggerEl?.contains(target as Node)) return;
      this.closePopover();
    };
    document.addEventListener('mousedown', this.clickListener, false);
  }

  private setupKeyListener(): void {
    this.keyListener = (event: KeyboardEvent) => {
      if (!this.popover) return;
      if (event.key === 'Escape') {
        this.closePopover();
      }
    };
    document.addEventListener('keydown', this.keyListener, false);
  }

  closePopover(): void {
    this.popover = false;
    this.removeEventListeners();
    this.cdr.detectChanges();
  }

  private removeEventListeners(): void {
    if (this.scrollListener) {
      document.removeEventListener('scroll', this.scrollListener, false);
    }
    if (this.clickListener) {
      document.removeEventListener('mousedown', this.clickListener, false);
    }
    if (this.keyListener) {
      document.removeEventListener('keydown', this.keyListener, false);
    }
  }

  // Navigate to author profile
  navigateToAuthor(): void {
    if (this.author.slug) {
      this.router.navigate(['/p', this.author.slug]);
      this.closePopover();
    }
  }

  // Get author location string
  getAuthorLocation(): string {
    return [this.author.address, this.author.institution, this.author.city, this.author.country]
      .filter(Boolean)
      .join(', ');
  }

  // Get author affiliation string (for inline display)
  getAuthorAffiliation(): string {
    return [this.author.institution, this.author.city, this.author.country]
      .filter(Boolean)
      .join(', ');
  }

  // Open email client
  openEmail(): void {
    if (this.author.email) {
      window.open(`mailto:${this.author.email}`, '_blank');
    }
  }

  // Open website
  openWebsite(): void {
    if (this.author.website) {
      window.open(this.author.website, '_blank');
    }
  }

  // Open ORCID profile
  openOrcid(): void {
    if (this.author.orcid) {
      const orcidUrl = this.author.orcid.startsWith('http') 
        ? this.author.orcid 
        : `https://orcid.org/${this.author.orcid}`;
      window.open(orcidUrl, '_blank');
    }
  }

  // Get popover position styles
  getPopoverStyle(): { [key: string]: string } {
    return {
      'position': 'fixed',
      'top': `${this.pos.y}px`,
      'left': `${this.pos.x}px`,
      'z-index': '9999'
    };
  }

  // Check if author has contact information
  hasContactInfo(): boolean {
    return !!(this.author.email || this.author.website || this.author.orcid || this.author.slug);
  }

  // Format ORCID for display
  getOrcidDisplay(): string {
    if (!this.author.orcid) return '';
    return this.author.orcid.replace('https://orcid.org/', '').replace('http://orcid.org/', '');
  }
}
